import React, { useCallback, useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import ToastContainer from '../../components/ToastContainer';
import { apiFetch } from '../../utils/apiClient';
import { invalidateCategories } from '../../components/CategoryPicker';
import { useVendor } from '../../contexts/VendorContext';

interface Category {
  id: number;
  name: string;
  handle: string | null;
  shopify_collection_id: string | null;
  collection_type: 'smart' | 'custom';
  is_active: boolean;
  on_store: boolean;
}

interface ToastData {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
}

const AdminCategories: React.FC = () => {
  const { settings } = useVendor();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [adding, setAdding] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [toasts, setToasts] = useState<ToastData[]>([]);
  const addToast = (message: string, type: ToastData['type']) =>
    setToasts((p) => [...p, { id: `${Date.now()}-${Math.random()}`, message, type }]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch('/api/admin/categories/?page_size=500');
      const data = await r.json();
      setCategories(data.results || data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setAdding(true);
    try {
      const r = await apiFetch('/api/admin/categories/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim() }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.name?.[0] || data.error || 'Could not add the category.');
      setName('');
      invalidateCategories();
      addToast(
        data.on_store ? `"${data.name}" added and created on BookMarketLk.` : `"${data.name}" added${data.store_error ? `, but not on the store yet: ${data.store_error}` : ' (store not connected).'}`,
        data.on_store ? 'success' : 'warning',
      );
      load();
    } catch (err: any) {
      addToast(err.message, 'error');
    } finally {
      setAdding(false);
    }
  };

  const syncFromStore = async () => {
    setSyncing(true);
    try {
      const r = await apiFetch('/api/admin/categories/sync/', { method: 'POST' });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || 'Could not read the store.');
      invalidateCategories();
      addToast(`Loaded from BookMarketLk: ${data.added} new, ${data.updated} updated.`, 'success');
      load();
    } catch (err: any) {
      addToast(err.message, 'error');
    } finally {
      setSyncing(false);
    }
  };

  const setActive = async (c: Category, active: boolean) => {
    setBusyId(c.id);
    try {
      const r = await apiFetch(`/api/admin/categories/${c.id}/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: active }),
      });
      if (!r.ok) throw new Error('Could not update the category.');
      invalidateCategories();
      load();
    } catch (err: any) {
      addToast(err.message, 'error');
    } finally {
      setBusyId(null);
    }
  };

  const push = async (c: Category) => {
    setBusyId(c.id);
    try {
      const r = await apiFetch(`/api/admin/categories/${c.id}/push/`, { method: 'POST' });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.store_error || data.error || 'Could not create the collection.');
      addToast(`"${c.name}" created on BookMarketLk.`, 'success');
      load();
    } catch (err: any) {
      addToast(err.message, 'error');
    } finally {
      setBusyId(null);
    }
  };

  const active = categories.filter((c) => c.is_active);
  const hidden = categories.filter((c) => !c.is_active);

  const row = (c: Category) => (
    <li key={c.id} className="px-4 py-3 flex flex-wrap items-center justify-between gap-2">
      <div className="min-w-0">
        <p className="font-medium text-gray-900">{c.name}</p>
        <p className="text-xs text-gray-500">
          {c.on_store ? (
            <span className="text-green-700">On BookMarketLk · {c.collection_type === 'smart' ? 'automatic (by tag)' : 'manual collection'}</span>
          ) : (
            <span className="text-orange-700">Not on the store yet</span>
          )}
          {c.handle && <> · /collections/{c.handle}</>}
        </p>
      </div>
      <div className="flex gap-2">
        {!c.on_store && settings.auto_sync && (
          <button disabled={busyId === c.id} onClick={() => push(c)} className="px-3 py-2 rounded-md border border-gray-300 text-sm min-h-[40px] disabled:opacity-50">
            Create on store
          </button>
        )}
        {c.is_active ? (
          <button disabled={busyId === c.id} onClick={() => setActive(c, false)} className="px-3 py-2 rounded-md border border-gray-300 text-gray-700 text-sm min-h-[40px] disabled:opacity-50">
            Hide
          </button>
        ) : (
          <button disabled={busyId === c.id} onClick={() => setActive(c, true)} className="px-3 py-2 rounded-md bg-blue-600 text-white text-sm min-h-[40px] disabled:opacity-50">
            Show
          </button>
        )}
      </div>
    </li>
  );

  return (
    <Layout>
      <ToastContainer toasts={toasts} onRemove={(id) => setToasts((p) => p.filter((x) => x.id !== id))} />
      <div className="w-full max-w-3xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Categories</h1>
            <p className="text-sm text-gray-500">Each category is a collection on BookMarketLk. Sellers pick from this list when adding a book.</p>
          </div>
          <button onClick={syncFromStore} disabled={syncing} className="px-4 py-2 rounded-md border border-gray-300 text-sm font-medium min-h-[44px] disabled:opacity-50">
            {syncing ? 'Loading…' : 'Load from BookMarketLk'}
          </button>
        </div>

        <form onSubmit={add} className="bg-white shadow rounded-lg p-4 mb-4 flex gap-2">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="New category, e.g. Sri Lankan History"
            className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-base sm:text-sm min-h-[44px]"
          />
          <button type="submit" disabled={adding || !name.trim()} className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium min-h-[44px] disabled:opacity-50">
            {adding ? 'Adding…' : 'Add'}
          </button>
        </form>

        {loading ? (
          <div className="text-center py-12"><div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>
        ) : (
          <>
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-3 border-b border-gray-100 text-sm font-semibold text-gray-900">Shown to sellers ({active.length})</div>
              {active.length === 0 ? (
                <p className="px-4 py-6 text-sm text-gray-500">No categories yet. Add one above or load them from the store.</p>
              ) : (
                <ul className="divide-y divide-gray-100">{active.map(row)}</ul>
              )}
            </div>
            {hidden.length > 0 && (
              <div className="bg-white shadow rounded-lg mt-4">
                <div className="px-4 py-3 border-b border-gray-100 text-sm font-semibold text-gray-500">Hidden ({hidden.length})</div>
                <ul className="divide-y divide-gray-100">{hidden.map(row)}</ul>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default AdminCategories;
